import { setSortOptions as setSortOptionsReducer } from '@mfs/client/listings-client/reducers/sortOptions/sortOptions'
import { Box, Button, ButtonChip, Text, getPaletteColor } from 'pcln-design-system'
import { ChevronDown } from 'pcln-icons'
import { Menu, MenuItem } from 'pcln-menu'
import React, { useCallback, useEffect, useMemo, useState, useTransition } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled, { css } from 'styled-components'
import { v4 as uuidv4 } from 'uuid'
import { logSortByOpen, logSortByOptionSelect } from '../../../../common/log-ga-event'
import { isDesktopVP } from '../../../util/displayHelpers'
import { isServerSide } from '../../../util/isServerSide'

const DropDown = styled(Button)`
  ${(props) =>
    props.isOpen &&
    css`
      outline: 2px solid ${getPaletteColor('primary.base')(props)};
    `}
`

export const SortOptions = ({
  changeHandler,
  numOfSlices,
  isDynamic = false,
  disabled = false,
  isStatic,
  overlayOpacity,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const activeTabId = useSelector((state) => state?.activeTabId)
  const isMobileDevice = useSelector((state) => state?.pclnBootstrapData?.isMobile)
  const sortOptionsDefaults = useSelector((state) => state?.filterInfo?.defaults?.SORT_OPTIONS)

  const dispatch = useDispatch()
  const sortOptions = useSelector((state) => state.sortOptions)
  const setSortOptions = useCallback(
    (sortOptions) => {
      dispatch(setSortOptionsReducer(sortOptions))
    },
    [dispatch]
  )

  const [, setTransition] = useTransition()

  const uniqueName = useMemo(() => uuidv4(), [])
  const isMobile = isServerSide ? isMobileDevice : !isDesktopVP()

  const SORT_OPTIONS = sortOptionsDefaults.reduce((acc, option) => {
    acc[option.value] = option.primaryLabel
    return acc
  }, {})

  useEffect(() => {
    // If return back to a previous slice selection,
    // reset sort type of later slices
    const newState = { ...sortOptions }
    if (numOfSlices > activeTabId) {
      for (let sliceId in newState) {
        if (Number(sliceId) > activeTabId) {
          newState[sliceId] = 'LOWEST_PRICE'
        }
      }

      setSortOptions(newState)
    }
  }, [activeTabId])

  const handleOpenDropdown = useCallback(() => {
    setIsOpen(!isOpen)
    !isOpen && logSortByOpen()
  }, [isOpen])

  const handleSortClick = (selected, option) => {
    if (selected) return

    const newSortOptions = {
      ...sortOptions,
      [activeTabId]: option,
    }
    setSortOptions(newSortOptions)
    logSortByOptionSelect(option)
    // Putting this behind setTransition let react render UI sooner
    setTransition(() => {
      changeHandler(newSortOptions)
    })
  }

  const menuButtonDesktop = (
    <DropDown
      variation='input'
      p='12px'
      width={1}
      data-testid='sort-dropdown'
      borderRadius='lg'
      isOpen={isOpen}
    >
      <Text regular>{SORT_OPTIONS[sortOptions[activeTabId]]}</Text>
      <ChevronDown ml='auto' />
    </DropDown>
  )

  const menuButtonMobile = (
    <ButtonChip
      px={isDynamic && 2}
      m={isStatic ? 0 : isDynamic ? '0 4px 0 4px' : '0 4px 0 0'}
      label={`Sort: ${SORT_OPTIONS[sortOptions[activeTabId]]}`}
      title={`Sort: ${SORT_OPTIONS[sortOptions[activeTabId]]}`}
      size={isDynamic || isStatic ? 'sm' : 'md'}
      id='sort-listings'
      selected
      showActionIcon
      tabIndex='0'
      disabled={disabled}
    />
  )

  const isSmallChip = isMobile || isStatic

  return (
    <Box mb={!isSmallChip && '24px'}>
      {!isSmallChip && !isDynamic && (
        <Text mb={2} bold>
          Sort By
        </Text>
      )}

      {/* This outer container is needed for event to fire, passing it in to Menu swallows the event */}
      <Box onClick={handleOpenDropdown}>
        <Menu
          p={0}
          width='208px'
          buttonNode={isSmallChip ? menuButtonMobile : menuButtonDesktop}
          isOpen={isOpen}
          trapFocus={false}
          querySelectorPortal={isSmallChip ? null : 'sort-options-filter-querySelectorPortal' + uniqueName}
          overlayOpacity={overlayOpacity}
        >
          {Object.keys(SORT_OPTIONS).map((option) => {
            const sortLabel = SORT_OPTIONS[option]
            const selected = option === sortOptions[activeTabId]
            return (
              <MenuItem key={option} onClick={() => handleSortClick(selected, option)} selected={selected}>
                <Text regular>{sortLabel}</Text>
              </MenuItem>
            )
          })}
        </Menu>
      </Box>
    </Box>
  )
}
